import { apiFetch } from './client'
import type { Denom, OHLCVResponse, Resolution } from './types'

export interface FetchOHLCVParams {
  poolAddr: string
  resolution: Resolution
  /** Unix seconds inclusive. Omit for server default (current). */
  from?: number
  /** Unix seconds inclusive. Omit for server default (now). */
  to?: number
  /** Bar count cap. Server defaults to 500, hard max 5000. */
  limit?: number
  /**
   * Price denomination. "usd" makes the backend multiply each candle by the
   * TON/USD rate of its own bucket (ADR-026). Omitted from the query for "ton"
   * (the default) so TON requests stay byte-identical and share the cache slot.
   */
  denom?: Denom
  phase?: 'launchpad'
  source?: string
  dex?: string
}

// fetchOHLCV is the only public call site for the OHLCV endpoint.
// useOHLCV hook composes around this; future SWR / react-query
// wrappers, prefetch caches, etc. layer on top without touching it.
export async function fetchOHLCV(p: FetchOHLCVParams, opts?: RequestInit): Promise<OHLCVResponse> {
  const params = new URLSearchParams()
  params.set('resolution', p.resolution)
  if (p.from != null) params.set('from', String(p.from))
  if (p.to != null) params.set('to', String(p.to))
  if (p.limit != null) params.set('limit', String(p.limit))
  // Only send denom for USD: TON is the server default, and omitting it keeps
  // the request (and the backend Valkey cache key) identical to the legacy call.
  if (p.denom === 'usd') params.set('denom', 'usd')
  if (p.phase) params.set('phase', p.phase)
  if (p.source) params.set('source', p.source)
  if (p.dex) params.set('dex', p.dex)
  const path = `/v1/pairs/${encodeURIComponent(p.poolAddr)}/ohlcv?${params.toString()}`
  const response = await apiFetch<OHLCVResponse>(path, opts)
  const requestedDenom = p.denom ?? 'ton'
  const responseDenom = response.denom ?? 'ton'
  if (responseDenom !== requestedDenom) {
    throw new Error(
      `OHLCV denomination mismatch: requested ${requestedDenom}, received ${responseDenom}`
    )
  }
  return response
}
